import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import {
  getLeadSegmentations,
  refreshLeadSegmentation,
  deleteLeadSegmentation,
} from '../services/api';

export default function SegmentationPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getLeadSegmentations();
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onRefresh = async (id) => {
    setRefreshingId(id);
    try {
      await refreshLeadSegmentation(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setRefreshingId(null);
    }
  };

  const onDelete = async (id, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete segment "${name}"? This removes all member assignments.`)) return;
    try {
      await deleteLeadSegmentation(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Segmentation
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/segmentation/new')}>
          New segment
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell>Last evaluated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {r.color && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.type} />
                  </TableCell>
                  <TableCell>
                    {r.ruleJson ? (
                      <Chip size="small" color="primary" icon={<RuleOutlinedIcon />} label={r.autoRefresh ? 'Rule (auto)' : 'Rule (manual)'} />
                    ) : r.isAutoCreated ? (
                      <Chip size="small" variant="outlined" label="AI" />
                    ) : (
                      <Chip size="small" variant="outlined" label="Manual" />
                    )}
                  </TableCell>
                  <TableCell align="right">{r.memberCount}</TableCell>
                  <TableCell>
                    {r.lastEvaluatedAt
                      ? new Date(r.lastEvaluatedAt).toLocaleString()
                      : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {r.ruleJson && (
                        <Tooltip title="Refresh now">
                          <span>
                            <IconButton
                              size="small"
                              disabled={refreshingId === r.id}
                              onClick={() => onRefresh(r.id)}
                            >
                              {refreshingId === r.id ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(`/segmentation/${r.id}`)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDelete(r.id, r.name)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      No segments yet. Click <b>New segment</b> to build a rule-based segment.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
